import posthog from 'posthog-js';

// Determine if we are in production
// Vite sets import.meta.env.PROD to true when running `vite build` or in production
const IS_PROD = import.meta.env.PROD;

/**
 * Initialize PostHog analytics
 * Should be called once at application startup
 */
export const initAnalytics = () => {
    // Only initialize if we have an API key
    const apiKey = import.meta.env.VITE_POSTHOG_KEY;
    const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

    if (apiKey && IS_PROD) {
        posthog.init(apiKey, {
            api_host: apiHost,
            // Disable autocapture to keep data clean and respect privacy (we'll track manually)
            autocapture: false,
            // Disable session recording by default (can be enabled via feature flag if needed)
            disable_session_recording: false,
            capture_pageview: false, // We handle routing manually or don't care about pageviews as much as events
            persistence: 'localStorage+cookie',
        });

        // Respect existing consent decision
        const consent = localStorage.getItem('morse_cookie_consent');
        if (consent === 'declined') {
            posthog.opt_out_capturing();
        }
    } else {
        if (!IS_PROD) {
            console.log('[Analytics] PostHog disabled in development (Set VITE_ENABLE_ANALYTICS_DEV=true to enable)');
        }
    }
};

/**
 * Track a specific event
 * @param {string} eventName - Name of the event (e.g., 'game_start')
 * @param {object} properties - Additional data to send (e.g., { mode: 'koch', wpm: 20 })
 */
export const trackEvent = (eventName, properties = {}) => {
    if (posthog.__loaded) {
        posthog.capture(eventName, properties);
    }

    // Log to console in development for debugging
    if (!IS_PROD) {
        console.log(`[Analytics] 📊 ${eventName}`, properties);
    }
};

/**
 * Identify a user (e.g., after login)
 * @param {string} userId - Unique user ID
 * @param {object} traits - User traits (e.g., { plan: 'pro' })
 */
export const identifyUser = (userId, traits = {}) => {
    if (posthog.__loaded) {
        posthog.identify(userId, traits);
    }

    if (!IS_PROD) {
        console.log(`[Analytics] 👤 Identify: ${userId}`, traits);
    }
};

/**
 * Reset user tracking (e.g., on logout)
 */
export const resetAnalytics = () => {
    if (posthog.__loaded) {
        posthog.reset();
    }
};

/**
 * Update analytics consent status
 * @param {boolean} accepted 
 */
export const setAnalyticsConsent = (accepted) => {
    if (posthog.__loaded) {
        if (accepted) {
            posthog.opt_in_capturing();
        } else {
            posthog.opt_out_capturing();
        }
    }
};
